/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useContext } from "react";
import { environment } from "@environment";
import { AnalyticsMetaData, getAnalyticTags } from "core/lib/analytics-methods";
import InterstitialPopup from "./interstitial-popup";
import context from "core/tv-guide.context";
import TvGuideService from "core/services/tv-guide.service";
import { AppContext } from "core/providers/tv-guide.context";
import Loader from "components/loader/loader";
import { PopupContents } from "core/types";
import RemindButton from "./remind-button";
import WatchButton from "./watch-button";
import VotingButton from "./voting-button";

class PopupLoader {
  static loading: any;
}

const PopupContent = (props: any) => {
  const appContext = useContext(AppContext);
  const store = appContext.store;

  const [popupContents, setPopupContents] = useState<any>(undefined);
  const [showInterstitialPopup, setShowInterstitialPopup] = useState(false);
  const [error, setError] = useState(false);

  const dictionary = context.dictionary;

  useEffect(() => {
    if (!props.popupInfo) {
      return;
    }

    const id = props.popupInfo.id;

    //prevent more than one fetch - was calling getProgrammeByEventId twice
    if (PopupLoader.loading === id) {
      return;
    }

    PopupLoader.loading = id

    const getData = async () => {
      ///const res = await getProgrammeByEventId(id);

      const data = (await new TvGuideService().getProgrammeByEventId(id)).data;

      let channelData;
      let chNumber;

      const channels = store.channels;

      if (props.channelNumber) {
        chNumber = props.channelNumber;
        channelData = channels[props.channelNumber];
      } else if (data.channel) {
        const match = Object.values(store.channels).find((program) => program.tag === data.channelTag);

        if (!match) {
          chNumber = store.currentChannel;
          channelData = channels[store.currentChannel];
          return;
        }

        chNumber = match.number;
        channelData = match;
      } else {
        setError(true);
      }

      const [dayName, month, dayNumber] = new Date(data.startTime)
        .toDateString()
        .split(" ");

      const mods = [];

      if (data.highDefinition) {
        mods.push("HD");
      }

      if (data.captions) {
        mods.push("CC");
      }

      const channelName = channelData ? channelData.name : data.channel;
      const dstvNowUrl = channelData?.dStvNowLink ?? "https://now.dstv.com/";
      const votingLink = channelData
        ? channelData.votingLink ?? ""
        : "";

      const popupContents: PopupContents = {
        channelIcon: data.logo,
        channelName: channelName,
        channelNumber: chNumber,
        showTitle: data.title,
        date: {
          // day: dayDictionary[dayName],
          day: dayName,
          dayNumber: dayNumber,
          month: month,
          time: data.startTime.replace(/(.*T)(\d{2}:\d{2})(.*)/, "$2"),
        },
        ratings: {
          age: data.rating,
          specifics: "",
        },
        mods: mods,
        thumbnailImage: data.thumbnailUri,
        synopsis: data.synopsis,
        link: {
          current: props.popupInfo.current,
          hide: props.popupInfo.hide,
          reminder: data.uniqueEventID,
          dstvNow: dstvNowUrl,
          votingLink: votingLink
        }
      };

      if (data.channel) {
        setPopupContents(popupContents);
      }

      //Old code used to do this if there was no channelData but it wasn't used.
      // let channelData;
      // if (props.channelNumber) {
      //   channelData =
      //     tvGuideData.dynamicData.programmeSchedule[props.channelNumber];
      // } else if (tvGuideData.dynamicData?.programmeSchedule) {
      //   channelData =
      //     tvGuideData.dynamicData.programmeSchedule[
      //     tvGuideData.currentChannel
      //     ];
      // }
    }

    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.popupInfo?.id]);

  if (error) {
    return (
      <>
        <div className="popup-header"></div>
        <div className="popup-body">
          <h3 className="text-center">Failed to fetch program details</h3>
        </div>
        <div className="popup-footer"></div>
      </>
    );
  }

  if (!popupContents) {
    return <Loader />
  }

  let remindMeUrl = `${environment.apiUrlBase}/umbraco/api/TVGuide/GetReminderByEventId?eventId=${popupContents.link.reminder}`;

  if (context.guideCountryCode) {
    remindMeUrl += `&country=${context.guideCountryCode}`;
  }

  if (context.guideUnit) {
    remindMeUrl += `&unit=${context.guideUnit}`;
  }

  if (showInterstitialPopup) {
    return <InterstitialPopup popupContents={popupContents} />;
  }

  const analytics = (name: string, cta: string, targetUrl: string = popupContents.link.dstvNow) => {
    const metaData: AnalyticsMetaData = {
      component_name: name,
      component_type: "show_modal",
      cta_text: cta,
      show_name: popupContents.showTitle,
      channel_name: popupContents.channelName,
      channel_number: popupContents.channelNumber,
      target_page_url: targetUrl
    }

    return getAnalyticTags(metaData);
  }

  const footerCssName = popupContents.link.votingLink !== "" && !popupContents.link.hide ? " text-center" : "";
  const placeholderImageUrl = "https://www.dstv.co.za/images/placeholder-Image.jpg";

  return (
    <>
      <div className="popup-header">
        <div className="channel-info">
          <img alt="channel-logo" src={popupContents.channelIcon} />
          <p className="channel-number">{popupContents.channelNumber}:</p>
          <p>{popupContents.channelName}</p>
        </div>
      </div>
      <div className="popup-body">
        <h3>{popupContents.showTitle}</h3>
        <div className="programme-short-info">
          <div className="programme-date">
            <p className="day">{popupContents.date.day}</p>
            <p className="day-number">{popupContents.date.dayNumber}</p>
            <p className="month">{popupContents.date.month}</p>
          </div>
          <p className="spacer">|</p>
          <p className="programme-time">{popupContents.date.time}</p>
          <p className="spacer">|</p>
          <div className="programme-rating">
            <p>{popupContents.ratings.age}</p>
            <p>{popupContents.ratings.specifics}</p>
          </div>

          <div className="programme-mods">
            {popupContents.mods.map((mod: any) => {
              return <p key={mod}>{mod}</p>;
            })}
          </div>
        </div>
        <img
          alt={popupContents.showTitle}
          src={popupContents.thumbnailImage}
          className="programme-hero-img"
          onError={(e: any) => {
            e.target.onError = null;
            e.target.src = placeholderImageUrl;
          }}
        />
        <p className="programme-synopsis">{popupContents.synopsis}</p>
      </div>
      <div className={`popup-footer${footerCssName}`}>
        <WatchButton
          analytics={analytics("show_watch_now", dictionary.watchNow)}
          popupContents={popupContents}
          setShowInterstitialPopup={setShowInterstitialPopup}
          dictionary={dictionary} />
        <RemindButton
          analytics={analytics("show_remind_me", dictionary.remindMe)}
          popupContents={popupContents}
          dictionary={dictionary}
          url={remindMeUrl} />
        <VotingButton
          analytics={analytics("channel_vote", dictionary.vote, popupContents.link.votingLink)}
          popupContents={popupContents}
          text={dictionary.vote} />
      </div>
    </>
  );
}

export default PopupContent;